import { useEffect, useMemo, useState } from 'react';
import type { GroupId, SkillSummary } from 'balus-common';

const cachedSkillsByGroupId = new Map<GroupId, SkillSummary[]>();
const inflightRequestsByGroupId = new Map<GroupId, Promise<SkillSummary[]>>();

export async function fetchBundledAIAssistantSkills(groupId: GroupId): Promise<SkillSummary[]> {
  const cached = cachedSkillsByGroupId.get(groupId);
  if (cached) return cached;

  const inflight = inflightRequestsByGroupId.get(groupId);
  if (inflight) return inflight;

  const request = (async () => {
    try {
      const response = await fetch(`/api/aichat/skills?groupId=${encodeURIComponent(groupId)}`, {
        headers: { 'x-application-name': 'balus-webapp' },
      });
      if (!response.ok) return [];

      const data = (await response.json()) as { skills?: unknown };
      const skills = Array.isArray(data.skills) ? (data.skills as SkillSummary[]) : [];
      cachedSkillsByGroupId.set(groupId, skills);
      return skills;
    } catch (error) {
      console.warn('Failed to load bundled AI assistant skills', error);
      return [];
    } finally {
      inflightRequestsByGroupId.delete(groupId);
    }
  })();

  inflightRequestsByGroupId.set(groupId, request);
  return request;
}

export function useBundledAIAssistantSkills(groupId: GroupId): {
  skills: SkillSummary[];
  loading: boolean;
  ready: Promise<SkillSummary[]>;
} {
  const ready = useMemo(() => fetchBundledAIAssistantSkills(groupId), [groupId]);
  const [skills, setSkills] = useState<SkillSummary[]>(() => cachedSkillsByGroupId.get(groupId) ?? []);
  const [loading, setLoading] = useState(!cachedSkillsByGroupId.has(groupId));

  useEffect(() => {
    let disposed = false;
    setSkills(cachedSkillsByGroupId.get(groupId) ?? []);
    setLoading(!cachedSkillsByGroupId.has(groupId));

    void ready.then((loadedSkills) => {
      if (disposed) return;
      setSkills(loadedSkills);
      setLoading(false);
    });

    return () => {
      disposed = true;
    };
  }, [groupId, ready]);

  return { skills, loading, ready };
}
